/**
 * Dynamic Cart Drawer Upsells - Priority-Based
 *
 * Shows 2 upsell products based on priority order (by sales volume).
 * Automatically filters out products already in cart.
 */

class DynamicUpsells {
  constructor() {
    this.upsellContainer = null;
    this.currentUpsellHandles = [];
    this.cartDrawerObserver = null;

    // Priority list of upsell products (by sales volume)
    this.PRIORITY_LIST = [
      'deskguard-glass-desk-mats',  // DeskGuard
      'glass-surface-cleaner',              // Repel
      'leather-felt-desk-pad',              // Leather Desk Pad
      'leather-footrest',                   // Footrest
      'wood-monitor-riser',                 // Monitor Riser
      'leather-valet-tray',                 // Valet Tray
      'wood-headphone-stand',               // Headphone Stand
      'leather-mouse-pad',                  // MousePad
      'mod-mop'                             // Mop
    ];

    // Never show this product
    this.EXCLUDED_HANDLE = 'hubless-office-chair-wheels';

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Listen for items being added to cart
    document.addEventListener('variant:add', (event) => this.handleCartAdd(event));

    // Listen for cart changes to update upsells
    document.addEventListener('cart:change', (event) => this.handleCartChange(event));

    // Listen for cart drawer opening to show upsells
    document.addEventListener('dialog:before-show', (event) => this.handleDialogOpen(event));

    // Set up MutationObserver to watch for cart drawer DOM updates
    this.setupCartDrawerObserver();

    // Also load upsells on initial page load if cart has items
    this.loadUpsellsOnPageLoad();
  }

  /**
   * Set up MutationObserver to watch for cart drawer DOM changes
   * The cart drawer innerHTML gets replaced after cart:change event,
   * so we need to refresh upsells after the DOM update completes
   */
  setupCartDrawerObserver() {
    const cartDrawer = document.querySelector('#cart-drawer');

    if (!cartDrawer) {
      return;
    }

    // Disconnect existing observer if any
    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }

    this.cartDrawerObserver = new MutationObserver((mutations) => {
      // Check if the cart-drawer__inner was modified
      const innerModified = mutations.some(mutation => {
        return mutation.target.classList?.contains('cart-drawer__inner') ||
               mutation.target.querySelector?.('.cart-drawer__inner');
      });

      if (innerModified) {
        this.refreshUpsellsAfterDOMUpdate();
      }
    });

    // Observe the cart drawer for changes to its children
    this.cartDrawerObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Refresh upsells after cart drawer DOM has been updated
   * Re-renders if container was wiped by DOM update
   */
  async refreshUpsellsAfterDOMUpdate() {
    // Clear cached container since DOM was just updated
    this.upsellContainer = null;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const upsellProducts = await this.determineUpsells(cart);
      const newUpsellHandles = upsellProducts.map(p => p.handle).sort().join(',');
      const currentUpsellHandles = this.currentUpsellHandles.sort().join(',');

      // Get the container to check if it's been wiped
      const container = this.getUpsellContainer();
      const containerIsEmpty = !container || container.innerHTML.trim() === '';

      // Re-render if upsells changed OR if container was wiped by DOM update
      if (newUpsellHandles !== currentUpsellHandles || containerIsEmpty) {
        if (upsellProducts.length > 0) {
          // Don't animate since this is just restoring after DOM update
          this.renderUpsells(upsellProducts, false);
        } else {
          this.hideUpsells();
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Load upsells on page load if cart already has items
   */
  async loadUpsellsOnPageLoad() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (cart.item_count > 0) {
        const upsellProducts = await this.determineUpsells(cart);

        if (upsellProducts.length > 0) {
          this.renderUpsells(upsellProducts);
        }
      }
    } catch (error) {
      // Silent error handling
    }
  }

  /**
   * Handle when cart drawer or dialog opens
   * @param {CustomEvent} event - dialog:before-show event
   */
  async handleDialogOpen(event) {
    // Check if it's the cart drawer opening
    const target = event.target;
    if (target && target.id === 'cart-drawer') {
      // Set up observer if not already done (cart drawer might not have been in DOM earlier)
      if (!this.cartDrawerObserver) {
        this.setupCartDrawerObserver();
      }

      // Clear cached container reference since cart drawer HTML may have been replaced
      this.upsellContainer = null;

      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const upsellProducts = await this.determineUpsells(cart);

        if (upsellProducts.length > 0) {
          this.renderUpsells(upsellProducts);
        } else {
          this.hideUpsells();
        }
      } catch (error) {
        // Silent error handling
      }
    }
  }

  /**
   * Get the upsell container (lazy loaded since cart drawer might not be in DOM yet)
   */
  getUpsellContainer() {
    if (!this.upsellContainer) {
      this.upsellContainer = document.querySelector('[data-dynamic-upsell-container]');
    }
    return this.upsellContainer;
  }

  /**
   * Handle when a product is added to cart
   * @param {CustomEvent} event - variant:add event with items and cart data
   */
  async handleCartAdd(event) {
    const { items, cart } = event.detail;

    if (!items || items.length === 0) {
      return;
    }

    // Determine which upsells to show
    const upsellProducts = await this.determineUpsells(cart);

    if (upsellProducts.length > 0) {
      this.renderUpsells(upsellProducts);
    } else {
      this.hideUpsells();
    }
  }

  /**
   * Handle cart changes to update upsells
   * @param {CustomEvent} event - cart:change event
   */
  async handleCartChange(event) {
    const { cart } = event.detail;

    // Clear cached container reference since cart drawer HTML may have been replaced
    this.upsellContainer = null;

    // Determine which upsells to show
    const upsellProducts = await this.determineUpsells(cart);

    if (upsellProducts.length > 0) {
      this.renderUpsells(upsellProducts);
    } else {
      this.hideUpsells();
    }
  }

  /**
   * Determine which 2 upsell products to show based on priority list
   * @param {Object} cart - The full cart object
   * @returns {Promise<Array>} - Array of up to 2 product objects
   */

  getGlassClarityFromCart(cart) {
    let clarity = null;

    cart.items.forEach(item => {
      const handle = this.getProductHandle(item.handle || item.url);
      const text = `${item.product_title || ''} ${item.variant_title || ''}`.toLowerCase();

      // You can tweak these checks if names differ on their store
      const isChairMat =
        handle.includes('glass-chair-mat') ||
        text.includes('glass chair mat');

      if (!isChairMat) return;

      if (text.includes('chiaro')) {
        clarity = 'Chiaro';
      } else if (text.includes('luminoso')) {
        clarity = 'Luminoso';
      }
    });

    return clarity;
  }

  async determineUpsells(cart) {
    // Get handles of products already in cart
    const cartHandles = cart.items.map(item => this.getProductHandle(item.handle || item.url));

    const availableProducts = [];

    this.deskGuardClarity = this.getGlassClarityFromCart(cart);
    // Walk through priority list
    for (const handle of this.PRIORITY_LIST) {
      // Skip if already in cart
      if (cartHandles.includes(handle)) continue;

      // Skip excluded product
      if (handle === this.EXCLUDED_HANDLE) continue;

      // Fetch product details
      const product = await this.fetchProductDetails(handle);

      if (product) {
        availableProducts.push(product);

        // Stop once we have 2 products
        if (availableProducts.length === 2) {
          break;
        }
      }
    }

    return availableProducts;
  }

  /**
   * Fetch full product details by handle
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>}
   */
  async fetchProductDetails(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Extract product handle from URL or handle string
   * @param {string} handleOrUrl - Handle or URL
   * @returns {string}
   */
  getProductHandle(handleOrUrl) {
    if (!handleOrUrl) return '';

    // If it's a URL, extract the handle
    if (handleOrUrl.includes('/products/')) {
      const match = handleOrUrl.match(/\/products\/([^/?]+)/);
      return match ? match[1] : handleOrUrl;
    }

    return handleOrUrl;
  }

  /**
   * Render the upsell products in the container
   * @param {Array} products - Array of 1-2 product objects
   * @param {boolean} animate - Whether to apply animation classes
   */
  renderUpsells(products, animate = true) {
    const container = this.getUpsellContainer();

    if (!container) {
      return;
    }

    if (!products || products.length === 0) return;

    this.currentUpsellHandles = products.map(p => p.handle);

    // Show the container
    container.style.display = 'block';

    // Create upsell HTML with optional no-animation class
    const upsellsHTML = this.createUpsellsHTML(products, animate);

    container.innerHTML = upsellsHTML;
  }

  /**
   * Create HTML for upsell products (1 or 2)
   * @param {Array} products - Array of product objects
   * @param {boolean} animate - Whether to include animation classes
   * @returns {string}
   */
  createUpsellsHTML(products, animate = true) {
    const productItems = products.map(product => this.createProductItemHTML(product)).join('');
    const animationClass = animate ? '' : ' no-animation';

    return `
      <div class="dynamic-upsell-container${animationClass}">
        <h3 class="dynamic-upsell-heading">You May Like These Too</h3>
        <div class="dynamic-upsell-grid">
          ${productItems}
        </div>
      </div>
    `;
  }

  /**
   * Create HTML for a single product item
   * @param {Object} product - Product data
   * @returns {string}
   */
  createProductItemHTML(product) {
    // Get the first available variant, or just the first variant if none available
    let variant = null;

    if (product.handle === 'deskguard-glass-desk-mats' && this.deskGuardClarity && product.variants && product.variants.length > 0) {
      const clarityLower = this.deskGuardClarity.toLowerCase();

      // Try to find a variant whose title/public_title includes the clarity
      variant =
        product.variants.find(v => {
          const text = `${v.public_title || v.title || ''}`.toLowerCase();
          return text.includes(clarityLower);
        }) ||
        product.variants.find(v => v.available) ||
        product.variants[0];
      product.title = variant.option1 + " - " + product.title;
    } else if (product.variants && product.variants.length > 0) {
      // Try to find an available variant first
      variant = product.variants.find(v => v.available) || product.variants[0];
    }

    if (!variant) {
      return '';
    }

    const price = this.formatMoney(variant.price || product.price || 0);
    const image = product.images?.[0] || product.image || product.featured_image || '';

    // if(window.innerWidth > 600) {
    //   return `
    //     <div class="dynamic-upsell-item">
    //       <div class="dynamic-upsell-item__info">
    //         <p class="dynamic-upsell-item__title">${product.title}</p>
    //         <div class="dynamic-upsell-item__image-wrapper">
    //           ${image ? `<img src="${image}" alt="${product.title}" loading="lazy" />` : ''}
    //         </div>
    //       </div>
    //       <p class="dynamic-upsell-item__price">${price} USD</p>
    //       <button
    //         class="dynamic-upsell-item__button"
    //         data-variant-id="${variant.id}"
    //         ${!variant.available ? 'disabled' : ''}
    //       >
    //         ${variant.available ? 'ADD' : 'SOLD OUT'}
    //       </button>
    //     </div>
    //   `;
    // }
    // else {
      return `
        <div class="dynamic-upsell-item">
          <div class="dynamic-upsell-item__image-wrapper">
            ${image ? `<img src="${image}" alt="${product.title}" loading="lazy" />` : ''}
          </div>
          <div class="dynamic-upsell-item-content">
            <p class="dynamic-upsell-item_title">${product.title}</p>
            <p class="dynamic-upsell-item__price">${price} USD</p>
          </div>
          <button
            class="dynamic-upsell-item__button"
            data-variant-id="${variant.id}"
            ${!variant.available ? 'disabled' : ''}
          >
            ${variant.available ? 'ADD' : 'SOLD OUT'}
          </button>
        </div>
      `
    // }
  }

  /**
   * Format money using simple dollar formatting
   * @param {number} cents - Price in cents
   * @returns {string}
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    return `$ ${dollars.toFixed(2)}`;
  }

  /**
   * Hide the upsell container
   */
  hideUpsells() {
    const container = this.getUpsellContainer();

    if (container) {
      container.style.display = 'none';
      container.innerHTML = '';
      this.currentUpsellHandles = [];
    }
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.DynamicUpsells = new DynamicUpsells();
}
